import { Injectable, inject } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    filter,
    iif,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap
} from 'rxjs';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { IJwtIdTokenClaims } from '@shs-digi-care-monorepo/interfaces';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AUTH_ENVIRONMENT } from '../models/environment.token';
import { UserActions } from '../../states';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private securityService = inject(OidcSecurityService);
    private http = inject(HttpClient);
    private router = inject(Router);
    private store = inject(Store);
    private environment = inject(AUTH_ENVIRONMENT);

    public isAuthenticated$: Observable<boolean>;
    private _authUser: BehaviorSubject<IJwtIdTokenClaims | undefined> = new BehaviorSubject<
        IJwtIdTokenClaims | undefined
    >(undefined);
    public authUser$: Observable<IJwtIdTokenClaims | undefined> = this._authUser.pipe(filter((user) => Boolean(user)));
    private _initialized = false;

    constructor() {
        this.isAuthenticated$ = this.securityService.checkAuth().pipe(
            switchMap((auth: LoginResponse) => {
                return combineLatest([of(auth), this.getUserInfo(auth)]);
            }),
            tap(([, userData]: [LoginResponse, unknown]) => {
                this._authUser.next(userData as IJwtIdTokenClaims);
            }),
            map(([auth]) => auth.isAuthenticated),
            shareReplay(1)
        );
    }

    private getUserInfo(auth: LoginResponse): Observable<unknown> {
        const url = window.location.pathname + window.location.search;
        return iif(
            () => auth.isAuthenticated,
            // angular-auth-oidc-client's "autoUserInfo" seems not to work with angular 17
            this.securityService.getAccessToken().pipe(
                filter(Boolean),
                switchMap((token) =>
                    this.http.get(this.environment.authentication.authWellknownEndpoints?.userInfoEndpoint as string, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })
                ),
                tap(() => {
                    if (this._initialized) return;
                    this._initialized = true;

                    const redirectUrl = window.localStorage.getItem('redirectUrl');
                    if (redirectUrl && redirectUrl !== '/') {
                        void this.router.navigateByUrl(redirectUrl);
                        window.localStorage.removeItem('redirectUrl');
                    } else {
                        if (url && url !== '/') {
                            this.router.navigateByUrl(url);
                        } else {
                            this.router.navigate(['/menu']);
                        }
                    }
                }),
                tap(() => {
                    this.store.dispatch(new UserActions.LoadMe());
                })
            ),
            of(null)
        );
    }

    public get authUser(): IJwtIdTokenClaims | undefined {
        return this._authUser.getValue();
    }

    public get accessToken$(): Observable<string> {
        return this.securityService.getAccessToken();
    }

    public login(): void {
        const url = window.location.pathname + window.location.search;
        window.localStorage.setItem('redirectUrl', url);
        this.securityService.authorize();
    }

    public logout(): void {
        this.securityService
            .isAuthenticated()
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                switchMap(() => this.securityService.logoffAndRevokeTokens()),
                catchError((err) => of(console.error(err)))
            )
            .subscribe();
    }
}
